import { LedgerAccount, Document, AddOn } from 'general-ledger/types';

type Client = {
  id: number;
  businessName: string;
  inboxState: string;
};

type Person = {
  id: string;
  fullName: string;
};

type TriggerContext =
  | CategorizeClientTransactionsTriggerContext
  | TriageClientDocumentsTriggerContext
  | BulkAdjustmentTriggerContext
  | CollectAllLedgerStatementsTriggerContext
  | ReviewGeneralLedgerTriggerContext
  | ResolveIncorrectAdjustmentTriggerContext
  | ResolveIncorrectBalanceTriggerContext
  | ResolveIncorrectCategorizationTriggerContext
  | ResolveIncorrectBooksTriggerContext
  | SetupNewConnectionTriggerContext;

type CategorizeClientTransactionsTriggerContext = {
  oldestTransactionDate?: string;
  numNeedsReview: number;
  numNeedsSuggestion: number;
  numOpenForInput: number;
  numAwaitingCategory?: number;
  totalAmountAwaitingCategory?: number;
};

type TriageClientDocumentsTriggerContext = {
  numUnknownDocs: number;
};

type BulkAdjustmentTriggerContext = {
  numMonthsAdjustmentRequired: number;
  startDate: string;
  endDate: string;
};

type CollectAllLedgerStatementsTriggerContext = {
  ledgerDateActive: string;
};

type ReviewGeneralLedgerTriggerContext = {
  startDate: string;
  endDate: string;
};

type ResolveIncorrectAdjustmentTriggerContext = {
  numMonths: number;
  startDate: string;
  endDate: string;
};

type ResolveIncorrectBalanceTriggerContext = {
  numMonths: number;
  startDate: string;
  endDate: string;
};

type ResolveIncorrectBooksTriggerContext = {
  numMonths: number;
  startDate: string;
  endDate: string;
};

type ResolveIncorrectCategorizationTriggerContext = {
  numMonths: number;
  startDate: string;
  endDate: string;
};

type SetupNewConnectionTriggerContext = {
  source: string;
  institution: string;
  numAccounts: number;
};

type JobParameters =
  | ResolveBalanceCheckerJobParameters
  | ResolvePlaidConnectionJobParameters
  | ResolveDocumentAdjustmentJobParameters
  | ResolveThirdPartyLoginAdjustmentJobParameters
  | ResolveBulkAdjustmentJobParameters
  | ResolveIncorrectAdjustmentJobParameters
  | ResolveIncorrectBalanceJobParameters
  | ResolveIncorrectCategorizationJobParameters
  | ResolveIncorrectBooksJobParameters
  | SetupNewConnectionJobParameters;

type ResolveBalanceCheckerJobParameters = {
  ledgerAccount?: Pick<LedgerAccount, 'id' | 'name'>;
};

type ResolvePlaidConnectionJobParameters = {
  ledgerAccount?: Pick<LedgerAccount, 'id' | 'name'>;
};

type ResolveDocumentAdjustmentJobParameters = {
  document?: Pick<Document, 'id' | 'name' | 'date'>;
  addOn?: Pick<AddOn, 'id' | 'name'>;
};

type ResolveThirdPartyLoginAdjustmentJobParameters = {
  addOn?: Pick<AddOn, 'id' | 'name'>;
  yearMonth: string;
};

type ResolveBulkAdjustmentJobParameters = {
  addOn?: Pick<AddOn, 'id' | 'name'>;
};

type ResolveIncorrectAdjustmentJobParameters = {
  reviewPeriod: string;
};

type ResolveIncorrectBalanceJobParameters = {
  reviewPeriod: string;
};

type ResolveIncorrectCategorizationJobParameters = {
  reviewPeriod: string;
};

type ResolveIncorrectBooksJobParameters = {
  reviewPeriod: string;
};

type SetupNewConnectionJobParameters = {
  connectionId: string;
};

type CollectMonthlyStatementJobParameters = {
  ledgerAccount?: Pick<LedgerAccount, 'id' | 'name'>;
  statementMonth?: string;
};

type CollectAllLedgerStatementsJobParameters = {
  ledgerAccount?: Pick<LedgerAccount, 'id' | 'name'>;
};

type JobParametersWithAddOn =
  | ResolveBulkAdjustmentJobParameters
  | ResolveDocumentAdjustmentJobParameters
  | ResolveThirdPartyLoginAdjustmentJobParameters;

type Task = {
  id: number;
  job: Job;
  type: TaskType;
  inboxType: InboxType;
  assignedPerson: Person | null;
  assigneeType: AssigneeType;
  status: TaskStatus;
  resolution: TaskResolution | null;
  createdOn: string;
  resolvedOn: string | null;
  assignedOn: string | null;
};

type Job = {
  id: number;
  client: Client;
  triggerContext?: TriggerContext | null;
  currentState: string;
  tasks: Task[];
  createdOn: string;
  type: JobType;
  priority?: number;
  dueDate?: string | null;
  escalatedOn?: string | null;
  escalatedBy?: Person | null;
  taskNotes: TaskNote[];
  parameters?: JobParameters;
};

type TaskNote = {
  id: number;
  task: Task;
  body?: string;
  createdBy: Person;
  createdOn: string;
};

type TaskQueryFilters = {
  clientIds?: string[];
  bookkeeperIds?: string[];
  taskIds?: string[];
  team?: Team;
  taskTypes?: TaskType[];
  jobTypes?: JobType[];
  inboxTypes?: InboxType[];
  status?: TaskStatus;
  assignedPersonId?: string | null;
  isEscalated?: boolean;
};

enum Team {
  DOCUMENT_SORTING = 'DOCUMENT_SORTING',
  CATEGORIZATION = 'CATEGORIZATION',
  ADJUSTMENTS = 'ADJUSTMENTS',
  ACCOUNT_BALANCING = 'ACCOUNT_BALANCING',
  THIRD_PARTY_LOGIN_STATEMENT_COLLECTION = 'THIRD_PARTY_LOGIN_STATEMENT_COLLECTION',
  QUALITY_REVIEW = 'QUALITY_REVIEW',
}

enum JobType {
  TRIAGE_CLIENT_DOCUMENTS = 'TRIAGE_CLIENT_DOCUMENTS',
  CATEGORIZE_CLIENT_TRANSACTIONS = 'CATEGORIZE_CLIENT_TRANSACTIONS',
  RESOLVE_DOCUMENT_ADJUSTMENT = 'RESOLVE_DOCUMENT_ADJUSTMENT',
  RESOLVE_BALANCE_CHECKER = 'RESOLVE_BALANCE_CHECKER',
  RESOLVE_PLAID_CONNECTION = 'RESOLVE_PLAID_CONNECTION',
  RESOLVE_3RD_PARTY_LOGIN_ADJUSTMENT = 'RESOLVE_3RD_PARTY_LOGIN_ADJUSTMENT',
  RESOLVE_BULK_ADJUSTMENT = 'RESOLVE_BULK_ADJUSTMENT',
  COLLECT_MONTHLY_STATEMENT = 'COLLECT_MONTHLY_STATEMENT',
  COLLECT_ALL_LEDGER_STATEMENTS = 'COLLECT_ALL_LEDGER_STATEMENTS',
  REVIEW_GENERAL_LEDGER = 'REVIEW_GENERAL_LEDGER',
  RESOLVE_INCORRECT_ADJUSTMENT = 'RESOLVE_INCORRECT_ADJUSTMENT',
  RESOLVE_INCORRECT_BALANCE = 'RESOLVE_INCORRECT_BALANCE',
  RESOLVE_INCORRECT_CATEGORIZATION = 'RESOLVE_INCORRECT_CATEGORIZATION',
  RESOLVE_INCORRECT_BOOKS = 'RESOLVE_INCORRECT_BOOKS',
  SETUP_NEW_CONNECTION = 'SETUP_NEW_CONNECTION',
}

enum TaskType {
  TRIAGE_ALL = 'TRIAGE_ALL',
  CATEGORIZE_ALL = 'CATEGORIZE_ALL',
  COLLECT_DOCUMENT_AND_MAKE_ADJUSTMENT = 'COLLECT_DOCUMENT_AND_MAKE_ADJUSTMENT',
  MAKE_ADJUSTMENT = 'MAKE_ADJUSTMENT',
  MAKE_BULK_ADJUSTMENT = 'MAKE_BULK_ADJUSTMENT',
  FIX_BALANCE = 'FIX_BALANCE',
  RETRY_FIX_BALANCE = 'RETRY_FIX_BALANCE',
  FIX_BALANCE_FOR_MAPPED_ACCOUNT = 'FIX_BALANCE_FOR_MAPPED_ACCOUNT',
  COLLECT_DOCUMENT = 'COLLECT_DOCUMENT',
  COLLECT_STATEMENT = 'COLLECT_STATEMENT',
  RETRY_COLLECT_STATEMENT = 'RETRY_COLLECT_STATEMENT',
  RESOLVE_THIRD_PARTY_LOGIN = 'RESOLVE_THIRD_PARTY_LOGIN',
  COLLECT_STATEMENTS = 'COLLECT_STATEMENTS',
  RETRY_COLLECT_STATEMENTS = 'RETRY_COLLECT_STATEMENTS',
  REVIEW_CATEGORIZATIONS = 'REVIEW_CATEGORIZATIONS',
  RESOLVE_INCOMPLETE_BOOKS = 'RESOLVE_INCOMPLETE_BOOKS',
  RETRY_REVIEW_CATEGORIZATIONS = 'RETRY_REVIEW_CATEGORIZATIONS',
  FIX_INCORRECT_ADJUSTMENT = 'FIX_INCORRECT_ADJUSTMENT',
  FIX_INCORRECT_BALANCE = 'FIX_INCORRECT_BALANCE',
  FIX_INCORRECT_CATEGORIZATION = 'FIX_INCORRECT_CATEGORIZATION',
  FIX_REVIEW_TOOL_ISSUES = 'FIX_REVIEW_TOOL_ISSUES',
  SETUP_AND_CONNECT_LEDGERS = 'SETUP_AND_CONNECT_LEDGERS',
  CONFIRM_CONNECTION_DETAILS = 'CONFIRM_CONNECTION_DETAILS',
}

enum JobEscalation {
  ESCALATED = 'ESCALATED',
  NON_ESCALATED = 'NON_ESCALATED',
}

enum JobPriority {
  HIGH = 3,
  NO_URGENCY = 0,
}

enum InboxType {
  KEEP_UP = 'KEEP_UP',
  CATCH_UP = 'CATCH_UP',
  TESTING = 'TESTING',
}

enum JobStatus {
  PENDING = 'PENDING',
  COMPLETE = 'COMPLETE',
}

enum TaskStatus {
  PENDING_WORK = 'PENDING_WORK',
  PAUSED = 'PAUSED',
  RESOLVED = 'RESOLVED',
  ALL = 'ALL',
}

enum TaskResolution {
  COMPLETE = 'COMPLETE',
  AUTO_FIXED = 'AUTO_FIXED',
  NO_ACTION_REQUIRED = 'NO_ACTION_REQUIRED',
  DOCUMENT_REQUIRED = 'DOCUMENT_REQUIRED',
  BPA_ACTION_REQUIRED = 'BPA_ACTION_REQUIRED',
  BPA_ACTION_REQUIRED_NO_WORK_COMPLETE = 'BPA_ACTION_REQUIRED_NO_WORK_COMPLETE',
  THIRD_PARTY_LOGIN_UNAVAILABLE = 'THIRD_PARTY_LOGIN_UNAVAILABLE',
  INSUFFICIENT_TRANSACTION_DATA = 'INSUFFICIENT_TRANSACTION_DATA',
  SPECIALIST_ACTION_REQUIRED = 'SPECIALIST_ACTION_REQUIRED',
  DUPLICATE_WORK = 'DUPLICATE_WORK',
  OTHER_TEAM_ACTION_REQUIRED = 'OTHER_TEAM_ACTION_REQUIRED',
  CONNECTION_DELETED = 'CONNECTION_DELETED',
  CANNOT_DELETE_CONNECTION = 'CANNOT_DELETE_CONNECTION',
  DOCUMENT_REQUESTED = 'DOCUMENT_REQUESTED',
}

enum AssigneeType {
  SPECIALIST = 'SPECIALIST',
  BOOKKEEPING_PORTFOLIO_ASSOCIATE = 'BOOKKEEPING_PORTFOLIO_ASSOCIATE',
  BOOKKEEPER = 'BOOKKEEPER',
  CLIENT = 'CLIENT',
}

export {
  Team,
  JobType,
  JobPriority,
  TaskType,
  InboxType,
  TaskStatus,
  JobStatus,
  AssigneeType,
  TaskResolution,
  JobEscalation,
};
// eslint-disable-next-line flowtype/no-types-missing-file-annotation
export type {
  Client,
  Person,
  Task,
  Job,
  TaskQueryFilters,
  TaskNote,
  JobParameters,
  ResolveDocumentAdjustmentJobParameters,
  ResolveBalanceCheckerJobParameters,
  ResolvePlaidConnectionJobParameters,
  ResolveThirdPartyLoginAdjustmentJobParameters,
  ResolveBulkAdjustmentJobParameters,
  CategorizeClientTransactionsTriggerContext,
  TriageClientDocumentsTriggerContext,
  BulkAdjustmentTriggerContext,
  ResolveIncorrectAdjustmentTriggerContext,
  CollectMonthlyStatementJobParameters,
  CollectAllLedgerStatementsJobParameters,
  CollectAllLedgerStatementsTriggerContext,
  ReviewGeneralLedgerTriggerContext,
  ResolveIncorrectAdjustmentJobParameters,
  ResolveIncorrectBalanceTriggerContext,
  ResolveIncorrectBalanceJobParameters,
  ResolveIncorrectBooksTriggerContext,
  ResolveIncorrectBooksJobParameters,
  ResolveIncorrectCategorizationTriggerContext,
  ResolveIncorrectCategorizationJobParameters,
  SetupNewConnectionTriggerContext,
  SetupNewConnectionJobParameters,
  JobParametersWithAddOn,
};
